import { nextTick, onMounted } from 'vue';

export default defineNuxtPlugin(() => {
  const router = useRouter();
  let currentRun = 0;

  const notifyClarityReady = () => {
    if (window.__clarityReady) {
      window.__clarityReady();
    }
  };

  const waitForStylesheets = async () => {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    );

    if (!links.length) return;

    await Promise.all(
      links.map(
        (link) =>
          new Promise<void>((resolve) => {
            if (link.sheet) {
              resolve();

              return;
            }

            const done = () => resolve();

            link.addEventListener('load', done, { once: true });
            link.addEventListener('error', done, { once: true });
          }),
      ),
    );
  };

  const waitForFonts = async () => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;

    if (fonts?.ready) {
      try {
        await fonts.ready;
      } catch (e) {
        // ignore
      }
    }
  };

  const waitForDomStability = (
    quietMs: number,
    maxWaitMs: number,
  ): Promise<void> =>
    new Promise((resolve) => {
      let finished = false;
      let quietTimer: number | undefined;

      const finish = () => {
        if (finished) return;
        finished = true;
        observer.disconnect();

        if (quietTimer) {
          clearTimeout(quietTimer);
        }

        resolve();
      };

      const resetQuiet = () => {
        if (quietTimer) {
          clearTimeout(quietTimer);
        }

        quietTimer = window.setTimeout(finish, quietMs);
      };

      const observer = new MutationObserver(resetQuiet);

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      resetQuiet();
      window.setTimeout(finish, maxWaitMs);
    });

  const waitForSmarticoReady = (runId: number, maxWaitMs: number) =>
    new Promise<void>((resolve) => {
      // Smartico já pronto
      if ((window as any)._smartico?.ready) {
        resolve();

        return;
      }

      const start = Date.now();
      const timer = window.setInterval(() => {
        // Se runId mudou, cancela (nova navegação)
        if (runId !== currentRun) {
          clearInterval(timer);
          resolve();

          return;
        }

        if (
          (window as any)._smartico?.ready ||
          Date.now() - start >= maxWaitMs
        ) {
          clearInterval(timer);
          resolve();
        }
      }, 300);
    });

  const waitForClarityBridge = (runId: number, maxWaitMs: number) =>
    new Promise<void>((resolve) => {
      if (window.__clarityReady) {
        resolve();

        return;
      }

      const start = Date.now();
      const timer = window.setInterval(() => {
        // Se runId mudou, cancela
        if (runId !== currentRun) {
          clearInterval(timer);
          resolve();

          return;
        }

        if (window.__clarityReady || Date.now() - start >= maxWaitMs) {
          clearInterval(timer);
          resolve();
        }
      }, 200);
    });

  const triggerClarityReady = () => {
    const runId = ++currentRun;

    (async () => {
      await nextTick();
      await new Promise(requestAnimationFrame);
      await new Promise(requestAnimationFrame);
      await waitForStylesheets();
      await waitForFonts();
      await waitForDomStability(400, 8000); // Reduzido: 8s max
      await waitForSmarticoReady(runId, 8000); // Reduzido: 8s max
      await waitForDomStability(800, 3000); // Reduzido: aguarda 800ms quiet
      await waitForClarityBridge(runId, 3000);

      if (runId !== currentRun) return;

      notifyClarityReady();
    })().catch(() => {});
  };

  onMounted(() => {
    triggerClarityReady();
  });

  router.afterEach(() => {
    triggerClarityReady();
  });
});
